import React, { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Link,
} from '@mui/material'
import GitHubIcon from '@mui/icons-material/GitHub'
import { colorPalette } from '../../style/colorPalette'

interface GitHubUsernameRegistrationDialogProps {
  open: boolean
  onClose: () => void
  onSave: (userName: string) => void
  currentUsername?: string
}

const GitHubUsernameRegistrationDialog: React.FC<
  GitHubUsernameRegistrationDialogProps
> = ({ open, onClose, onSave, currentUsername = '' }) => {
  const intl = useIntl()
  // より安全な初期化
  const [userName, setUsername] = useState(currentUsername || '')
  const [error, setError] = useState<string>('')

  useEffect(() => {
    if (open) {
      setUsername(currentUsername || '')
      setError('')
    }
  }, [currentUsername, open])

  const validateUsername = (value: string): string => {
    // 空値は許可（削除可能）
    if (!value) {
      return ''
    }

    const githubUserNamePattern = /^[a-zA-Z0-9-]{1,39}$/
    if (!githubUserNamePattern.test(value)) {
      return intl.formatMessage({ id: 'github.username.invalid.format' })
    }

    return ''
  }

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = (event?.target?.value || '').trim()
    setUsername(newValue)

    if (newValue) {
      const validationError = validateUsername(newValue)
      setError(validationError)
    } else {
      setError('')
    }
  }

  const handleSave = () => {
    try {
      const validationError = validateUsername(userName)

      if (validationError) {
        setError(validationError)
        return
      }

      if (onSave && typeof onSave === 'function') {
        onSave(userName)
      }
    } catch (error) {
      setError(intl.formatMessage({ id: 'github.save.error' }))
    }
  }

  const handleClose = () => {
    try {
      setUsername(currentUsername || '')
      setError('')
    } catch (error) {
      // エラーハンドリング
    } finally {
      if (onClose && typeof onClose === 'function') {
        onClose()
      }
    }
  }

  const isValid = Boolean(!error)

  if (!open) {
    return null
  }

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth={true}
      aria-labelledby="github-userName-dialog-title"
    >
      <DialogTitle id="github-userName-dialog-title">
        {intl.formatMessage({ id: 'github.dialog.title' })}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth={true}
            label={intl.formatMessage({ id: 'github.username.label' })}
            value={userName || ''}
            onChange={handleUsernameChange}
            error={Boolean(error)}
            helperText={
              error || intl.formatMessage({ id: 'github.username.helper' })
            }
            placeholder="octocat"
            autoFocus={true}
            variant="outlined"
          />

          {userName && !error && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: colorPalette.monotone[1],
                borderRadius: 1,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom={true}
              >
                {intl.formatMessage({ id: 'github.preview.title' })}
              </Typography>
              <Link
                href={`https://github.com/${userName}`}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                underline="hover"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 'medium',
                }}
              >
                <GitHubIcon sx={{ mr: 1, fontSize: 18 }} />
                https://github.com/{userName}
              </Link>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                {intl.formatMessage({ id: 'github.preview.confirmation' })}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          {intl.formatMessage({ id: 'dialog.cancel' })}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isValid}
          startIcon={<GitHubIcon />}
        >
          {intl.formatMessage({ id: 'dialog.register' })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default GitHubUsernameRegistrationDialog
